/* eslint-disable max-depth */
/* eslint-disable max-lines */
import {assert} from 'ts-essentials'
import {Lang} from 'yougile-lang'
import {
  ArrUtil,
  ensure,
  ffLazyLoading,
  hasFeatureFlag,
  Listener,
  logger,
  Option,
  SetWithExpiration,
  Time,
  Util,
  UUID
} from 'yougile-util'
import {
  Branch,
  BranchJSON,
  Connections,
  ConnectionsJSON,
  DataAddr,
  DataObject,
  DataObjectAddress,
  DataObjectId,
  DataObjectJSON,
  Diff,
  Entity,
  EntityJSON,
  GenResp,
  googleCalendarRedirectPage,
  Hub,
  HubLike,
  IndexReq,
  IndexResp,
  IndexV2Req,
  IndexV2Resp,
  minorDataTransmissionVersion,
  newsCompanyId,
  Path,
  Sandbox,
  SandboxData,
  SandboxJSON,
  SandboxTree,
  User
} from 'yougile-model'
import {
  getLicenseUserCount,
  getUserCount
} from '@components/dashboard/dashboard-utils'
import {pendingCompanyUserStore} from '@components/logged-in/pending-company-user-store'
import {registerIndexLoadDuration} from '@components/team/lazy-monitor/lazy-events'
import {PageService} from '@extensions/page-service'
import {Backend} from '../../common/backend'
import {Debouncer} from '../../common/debouncer'
import {MessageBox} from '../components/message-box'
import {Company} from '../extensions/company'
import {globals} from '../extensions/globals-base'
import {Search} from '../extensions/search'
import {Accounts} from './accounts'
import {addNotAcceptedToSearchParams} from './add-not-accepted-to-search-params'
import {Cache, CacheInitOptions} from './cache'
import {Connector} from './connector'
import {DevUtils} from './dev-utils'
import {MarketplaceClient} from './marketplace-client'
import {PaymentClient} from './payment-client'
import {PermissionsChecker} from './permissions-checker'
import {Refresh} from './refresh'
import {Storage, wasLicenseWarningShownKey} from './storage'
import {SyncCommitHandler} from './sync-commit-handler'
import {SyncEvents, SyncUpdate} from './sync-events'

const unapprovedChangesKey = 'unapprovedChanges'
const actualizeDebouncer = new Debouncer(3000)
const writeCacheTimeout = 100_000

interface UnapprovedSerialized {
  checksum: number
  changes: {[branchId: string]: BranchJSON}
}

export const syncLoadListener = new Listener()

interface SyncInitOptions {
  readonly userId: DataObjectId
  readonly key: string
  readonly callback: (response?: GenResp) => void
  readonly cacheService: CacheInitOptions
  readonly onReload?: (companyId?: DataObjectId) => void
  readonly onBeforeReload?: () => void
  readonly selectedCompanyIds?: DataObjectId[]
}

interface SyncReloadOptions {
  readonly initial: boolean
  readonly callback?: (response: GenResp) => void
  readonly shouldUseCachedData?: boolean
  readonly force?: boolean
  readonly selectedCompanyIds?: ReadonlyArray<DataObjectId>
}

export class SyncAutoSaver {
  public static init(): void {
    clearInterval(SyncAutoSaver.upId)
    SyncAutoSaver.upId = setInterval(() => {
      try {
        SyncAutoSaver.routine()
      } catch (e) {
        logger.warn(e)
      }
    }, 100_000)
  }
  public static update(): void {
    SyncAutoSaver.updates++
  }
  private static upId: TimeoutHandler
  private static updates = 0
  private static routine(): void {
    if (SyncAutoSaver.updates > 0) {
      SyncAutoSaver.updates = 0
      void Sync.saveCachedData()
    }
  }
}

const LAZY_OBJECT_DIFF_PATHS = {
  chat: true,
  stickers: true,
  complete: true,
  deleted: true,
  location: true,
  revision: true,
  timestamp: true,
  properties: true
} as const

/**
 * Manages data synchronization with server.
 */
export class Sync {
  private static canSaveCache = false
  private static selectedCompanyIds: ReadonlyArray<DataObjectId> | undefined

  private static userId: DataObjectId
  private static key: string
  private static onReload: SyncInitOptions['onReload']
  private static onBeforeReload: () => void
  private static saveCacheInProgress: number | undefined

  public static processedBranchesIds: SetWithExpiration<UUID> =
    new SetWithExpiration(24 * 3600 * 1000, 80 * 1000)
  static localBranchesIds: SetWithExpiration<UUID> = new SetWithExpiration(
    24 * 3600 * 1000,
    80 * 1000
  )

  private static sandboxes: Map<DataObjectId, SandboxTree> = new Map()

  public static loadedAt = 0
  public static latestReload = 0
  public static reloading = false

  private static actualizing: boolean
  private static actualizeTime: number

  public static newsSandbox?: SandboxTree

  public static isNewsEditor = false

  public static availableCompanies?: ReadonlyArray<{
    companyId: DataObjectId
    companyName: string
  }>
  public static pendingCompanies?: DataObjectId[]

  public static searchIndices: Map<DataObjectId, Search> = new Map()
  public static profileId?: DataObjectId

  static commitDebouncer = new Debouncer<Branch>(300)
  /**
   * This collection contains the most recent revisions of sandboxes,
   * that were received from server by full /data/index request or by
   * some branch update. It is used for comparing with current data state
   * in order to calculate diffs of objects that are needed to be send to
   * the server.
   */
  public static latestRemoteData: Map<DataObjectId, SandboxJSON> = new Map()

  /**
   * Branches that were commited but was not processed (sent to server) yet.
   */
  static unprocessedChanges: Map<UUID, Branch> = new Map()

  /**
   * Branches that were sent to server but were not yet arrived as
   * a result of a pull request from server.
   */
  static unapprovedChanges: Map<UUID, Branch> = new Map()

  /**
   * Tracks local objects that are registered for synchronization (i.e. these
   * objects are tracked for local changes).
   * Also provides a way to get dataObject by its id, so it is possible to get
   * registered dataObject knowing the corresponding dataObjectJson
   * Map: companyId -> Man(dataObject.id -> dataObject)
   */
  private static registeredObjects: Map<
    DataObjectId,
    Map<DataObjectId, DataObject>
  > = new Map()

  /**
   * Stores handlers that are called when server approved receiving new
   * data object with the specified id.
   */
  private static serverCreateApproveHandlers: Map<DataAddr, () => void> =
    new Map()
  private static serverUpdateApproveHandlers: Map<DataAddr, Set<() => void>> =
    new Map()

  public static invalidateCache(companyId?: DataObjectId): Promise<void> {
    Sync.canSaveCache = false
    return Sync.cleanCachedData(companyId)
  }

  public static onServerApprovedUpdate(
    companyId: DataObjectId,
    objId: DataObjectId,
    cb: () => void
  ): () => void {
    const addr = DataObjectAddress.create(companyId, objId)
    if (!Sync.serverUpdateApproveHandlers.has(addr)) {
      Sync.serverUpdateApproveHandlers.set(addr, new Set())
    }
    const handlers = Sync.serverUpdateApproveHandlers.get(addr)
    assert(handlers)
    handlers.add(cb)
    return () => handlers.delete(cb)
  }

  public static onServerApprovedCreation(
    companyId: DataObjectId,
    objId: DataObjectId,
    cb: () => void
  ): void {
    if (Sync.latestRemoteData.get(companyId).index[objId]) {
      cb()
    } else {
      const addr = DataObjectAddress.create(companyId, objId)
      if (Sync.serverCreateApproveHandlers.has(addr)) {
        logger.warn('Server approve handler already set!')
        return
      }
      Sync.serverCreateApproveHandlers.set(addr, cb)
    }
  }

  public static listSandboxes(): SandboxTree[] {
    return [...Sync.sandboxes.values()]
  }

  public static getFeedbackCompany(): SandboxTree {
    return Sync.getProfile()
  }

  public static getProfile(): SandboxTree {
    return Sync.sandboxes.get(Sync.profileId)
  }

  public static getCurrentSandbox(): SandboxTree {
    return Sync.sandboxes.get(globals.getCompanyId())
  }

  public static getCurrentUser(): User {
    return Sync.getProfile().get(globals.currentUserId) as User
  }

  public static getOtherUserIds(): DataObjectId[] {
    const res = new Set<DataObjectId>()
    ;[...Sync.sandboxes.values()]
      .filter(
        b => b.companyId !== newsCompanyId && b.companyId !== Sync.profileId
      )
      .forEach(b => {
        b.getAllUsers(false).forEach(u => u?.id && res.add(u.id))
      })
    return [...res].filter(id => id !== globals.currentUserId)
  }

  public static findSandboxByPath(path: Path): SandboxTree {
    const companyPart = '/' + path.split('/')[1]
    for (const s of Sync.listSandboxes()) {
      if (s.startPath() === companyPart) {
        return s
      }
    }
    return undefined
  }

  static getOrCurrent(obj?: {companyId: DataObjectId}): SandboxTree {
    return obj ? this.get(obj) : this.getCurrentSandbox()
  }

  /**
   * It is often needed to get sandbox for some object,
   * this method is for code brevity.
   */
  public static get(
    obj: {companyId: DataObjectId},
    allowUndefined = false
  ): SandboxTree {
    if (!obj) {
      return undefined
    }
    if (Sync.newsSandbox && obj.companyId === Sync.newsSandbox.companyId) {
      return Sync.newsSandbox
    }
    const res = Sync.sandboxes.get(obj.companyId)
    if (!res && !allowUndefined) {
      logger.warn('No such sandbox', obj.companyId)
    }
    return res
  }

  /**
   * Reloads all the data from server and starts new synchronization session.
   */
  public static async reload({
    initial,
    shouldUseCachedData,
    force,
    callback,
    selectedCompanyIds
  }: SyncReloadOptions): Promise<void> {
    if (Sync.reloading) {
      logger.warn('Sync.reload: already reloading')
      return
    }
    if (!initial) {
      logger.log('Sync.reload', initial, shouldUseCachedData, force)
    }
    if (!!initial && !Sync.loadedAt) {
      Sync.loadedAt = Time.now()
    }
    // not too frequently
    if (
      !force &&
      !initial &&
      Sync.latestReload &&
      Time.now() - Sync.latestReload < 20000
    ) {
      logger.warn('skipping reload (too frequent)')
      return
    }
    Sync.reloading = true
    Sync.latestReload = Time.now()
    if (!initial) {
      Sync.onBeforeReload()
    }

    Sync.restoreUnapproved()

    Connector.cleanup()
    Sync.canSaveCache = false
    Sync.sandboxes.clear()
    Sync.searchIndices.clear()
    Sync.latestRemoteData.clear()
    Sync.unprocessedChanges.clear()

    const t1 = Date.now()
    Sync.selectedCompanyIds = selectedCompanyIds ?? Sync.selectedCompanyIds
    const data: {response: IndexResp; hasRemoteData: boolean} =
      shouldUseCachedData && (await Sync.loadData(Sync.selectedCompanyIds))
    if (data) {
      const t2 = Date.now()
      if (t2 - t1 > 2000) {
        logger.log('Cache done', t2 - t1 + 'ms')
      }
      Sync.processIndexResp(data.response, !data.hasRemoteData, callback)
    } else {
      await Sync.loadRemoteData(Sync.selectedCompanyIds).then(resp => {
        logger.log('Index done', Date.now() - t1 + 'ms')
        Sync.processIndexResp(resp, false, callback)
      })
    }
  }

  private static addPage(company: SandboxJSON, companyPage: SandboxJSON): void {
    for (const id of Object.keys(companyPage.index)) {
      company.index[id] = companyPage.index[id]
    }
  }

  private static async loadRemoteData(
    companyIds?: ReadonlyArray<DataObjectId>
  ): Promise<IndexResp> {
    const withLazyTasks = hasFeatureFlag(ffLazyLoading)
    const indexReq: IndexReq = {
      userId: Sync.userId,
      key: Sync.key,
      paging: true,
      ...(companyIds ? {companies: [...companyIds]} : {}),
      withLazyTasks
    }
    await DevUtils.delay(5000)(0)

    try {
      const startTime = Date.now()
      const res = await Backend.postJSON<IndexResp>('/data/index', indexReq)
      if (res.result !== 'ok') {
        return res
      }

      for (const company of res.data) {
        const {page, total} = company.indexPaging ?? {}

        if (total && page) {
          let current = page
          while (current < total) {
            current++
            const part = await Backend.postJSON<IndexResp>('/data/index', {
              userId: Sync.userId,
              key: Sync.key,
              paging: true,
              page: current,
              total: total,
              companies: [company.companyId],
              withLazyTasks
            })
            const companyPart = part.data.find(
              c => c.companyId === company.companyId
            )
            if (companyPart) {
              this.addPage(company, companyPart)
            } else {
              logger.error(
                'Incorrect company part',
                company.companyId,
                part.data?.map(p => p.companyId),
                part.data.length
              )
              throw new Error('Incorrect company part')
            }
          }
          delete company.indexPaging
        }
      }
      const endTime = Date.now()
      const duration = endTime - startTime
      registerIndexLoadDuration(duration)
      return res
    } catch (error) {
      return {
        result: 'error',
        error: error instanceof Error ? error.message : error
      } as IndexResp
    }
  }

  private static processIndexResp(
    resp: IndexResp,
    isFromCache: boolean,
    cb?: (r: GenResp) => void
  ): void {
    if (resp && resp.result === 'ok') {
      globals.standalone = resp.standalone
      globals.devMode = resp.devMode
      globals.readonly = globals.readonly || resp.readonly

      if (resp.allowUploadExtensions) {
        globals.allowUploadExtensions = true
      }
      if (resp.enabledExtensions) {
        globals.initEnabledExtensions = resp.enabledExtensions
      }

      MarketplaceClient.apps = resp.apps || []
      Sync.fillInitialDataFromResponse(resp, isFromCache, r => {
        Sync.reloading = false
        if (cb) {
          cb(r)
        }
        if (!isFromCache) {
          SyncAutoSaver.update()
        }
      })
    } else if (resp && resp.error === 'Authentication required') {
      logger.log('Sync auth, refreshing…')
      Backend.report(
        'Clear cred on auth required after index',
        globals.currentUserId
      )
      void Refresh.clearCredentials()
      Sync.reloading = false
    } else if (resp && resp.error === 'company not accepted') {
      location.search = addNotAcceptedToSearchParams()

      void Refresh.clearCredentials(false)
      Sync.reloading = false
    } else {
      Sync.reloading = false
      // todo: implement network errors handling
      logger.error('???', resp)
      throw new Error('Not implemented')
    }
  }

  private static tryReuseObject(
    companyId: DataObjectId,
    id: DataObjectId
  ): DataObject | undefined {
    const c = Sync.registeredObjects.get(companyId)
    if (c) {
      return c.get(id)
    } else {
      return undefined
    }
  }

  private static fillInitialDataFromResponse(
    resp: IndexResp,
    isFromCache: boolean,
    callback?: (resp: GenResp) => void
  ): void {
    Sync.profileId = resp.profileId
    const sandboxes: Map<DataObjectId, SandboxTree> = new Map()
    if (resp.news) {
      Sync.newsSandbox = SandboxTree.fromJsonData(
        resp.news,
        Sync.tryReuseObject,
        Sync.newsSandbox
      )
      Sync.latestRemoteData.set(resp.news.companyId, resp.news)
      Sync.isNewsEditor = resp.isNewsEditor
    }
    if (resp.phone) {
      globals.phone = resp.phone
    }
    if (resp.registrationType) {
      globals.registrationType = resp.registrationType
    }

    if (resp.availableCompanies) {
      Sync.availableCompanies = resp.availableCompanies
    }
    if (resp.pendingCompanies) {
      Sync.pendingCompanies = resp.pendingCompanies
      pendingCompanyUserStore.update()
    }

    resp.data.forEach((data: SandboxJSON) => {
      if (!data.companyId || !data.users || !data.index || !data.revision) {
        logger.error('Incorrect data received, skipping company…')
        return
      }
      const sb = SandboxTree.fromJsonData(
        data,
        Sync.tryReuseObject,
        Sync.get(data, true)
      )
      sandboxes.set(data.companyId, sb)
      Sync.latestRemoteData.set(data.companyId, data)
    })
    Sync.sandboxes = sandboxes
    try {
      Sync.unapprovedChanges.forEach(b => {
        if (!b.approved) {
          Sync.applyBranch(b)
        }
      })
    } catch (e) {
      logger.warn('Failed to apply unapporved changes', e)
    }
    Accounts.initialLoad(resp.accounts)
    PaymentClient.loadAll(resp.paymentStatuses, resp.paymentUsersStatus)

    Sync.canSaveCache = true

    Sync.invokeCallback(resp, callback)
    const actualize = isFromCache
      ? () => Sync.actualizeDataFromServer(undefined)
      : () => Promise.resolve(false)
    void actualize()
      .catch(e => {
        logger.warn(e)
      })
      .then(() => {
        Sync.initConnector()
        Util.catch(() => Sync.initSearchIndices(sandboxes))
        void syncLoadListener.trigger()
      })
  }

  static processSandboxData(c: SandboxData): void {
    const sb =
      c.id === newsCompanyId ? Sync.newsSandbox : Sync.get({companyId: c.id})
    if (!sb) {
      if (!c.data) {
        logger.warn('Received unknown company', c)
        return
      }
      logger.log('Received new company')
      if (!c.id || !c.users || !c.data || !c.revision) {
        logger.error('Incorrect data received, skipping company…')
        return
      }
      const sbJson: SandboxJSON = {
        companyId: c.id,
        index: c.data,
        revision: c.revision,
        users: c.users
      }
      const nsb = SandboxTree.fromJsonData(sbJson, Sync.tryReuseObject)
      if (c.id !== newsCompanyId) {
        if (nsb) {
          Sync.sandboxes.set(c.id, nsb)
        }
      } else {
        Sync.newsSandbox = nsb
      }
      if (c.accounts) {
        Accounts.initialLoad(c.accounts)
      }

      Sync.latestRemoteData.set(c.id, sbJson)
      SyncEvents.triggerNewCompany(c.id)
      return
    }
    if (c.accounts) {
      Accounts.initialLoad(c.accounts)
    }

    sb.updateUsers(c.users)
    if (c.branches) {
      const br = c.branches
      br.sort((a, b) => a.toRevision - b.toRevision)
      br.forEach(b => {
        Sync.processRemoteChanges(b)
        if (b.toRevision && b.toRevision > sb.revision) {
          sb.revision = b.toRevision
        }
      })
      logger.log('Sync actualize: branch')
    } else if (c.patch) {
      Sync.applyPatch(c.id, c.revision, c.patch, 'all')
      logger.log('Sync actualize: patch', Object.keys(c.patch).length)
    } else if (c.data) {
      logger.log('Sync actualize: data')
      logger.warn('Sync: received data from index-v2', sb.companyId)
      Sync.applyPatch(c.id, c.revision, c.data, 'silent')
      SyncEvents.triggerAll(c.id)
    } else {
      // actual
    }
  }

  public static actualizeDataFromServer(
    // if this is undefined, then server returns all companies
    companyId: DataObjectId | undefined
  ): Promise<boolean> {
    if (!Backend.features.actualize) {
      return Promise.resolve(false)
    }
    if (!Sync.key || !Sync.userId) {
      return Promise.resolve(false)
    }
    if (Sync.actualizeTime > Time.now() - 20_000) {
      logger.log('Too frequent actualizing, debouncing…')
      actualizeDebouncer.runButNotOften(() =>
        this.actualizeDataFromServer(companyId)
      )
      return Promise.resolve(true)
    }
    // There is no need to actualize data for google calendar redirect page
    if (PageService.getUrl().includes(googleCalendarRedirectPage)) {
      return Promise.resolve(true)
    }
    if (Sync.actualizing) {
      if (Sync.actualizeTime && Sync.actualizeTime > Time.now() - 10 * 60_000) {
        logger.log('Already actualizing, debouncing…')
        actualizeDebouncer.runButNotOften(() =>
          this.actualizeDataFromServer(companyId)
        )
        return Promise.resolve(true)
      }
    }
    Sync.actualizing = true
    Sync.actualizeTime = Time.now()

    const company = companyId ? Sync.get({companyId}) : undefined
    const companies = company
      ? [company]
      : [
          ...Sync.listSandboxes(),
          ...(Sync.newsSandbox ? [Sync.newsSandbox] : [])
        ]

    const req: IndexV2Req = {
      userId: Sync.userId,
      key: Sync.key,
      minorVersion: minorDataTransmissionVersion,
      acceptAll: !companyId,
      companies: companies.map(b => ({
        id: b.companyId,
        revision: b.revision
      })),
      withLazyTasks: hasFeatureFlag(ffLazyLoading)
    }

    return new Promise<boolean>(resolve => {
      void Backend.postJSON<IndexV2Resp>('/data/index-v2', req)
        .then(json => {
          globals.standalone = json.standalone
          globals.devMode = json.devMode
          globals.readonly = globals.readonly || json.readonly

          if (json.phone) {
            globals.phone = json.phone
          }

          if (json.availableCompanies) {
            Sync.availableCompanies = json.availableCompanies
          }
          if (json.pendingCompanies) {
            Sync.pendingCompanies = json.pendingCompanies
            pendingCompanyUserStore.update()
          }
          if (json.allowUploadExtensions) {
            globals.allowUploadExtensions = true
          }
          if (json.enabledExtensions) {
            globals.initEnabledExtensions = json.enabledExtensions
          }

          if (json.availableCompanies) {
            try {
              const available = new Set(
                json.availableCompanies.map(c => c.companyId)
              )
              Sync.listSandboxes().forEach(sb => {
                if (
                  sb.companyId !== Sync.profileId &&
                  sb.companyId !== newsCompanyId &&
                  !available.has(sb.companyId)
                ) {
                  logger.log('Actualize: deleting company', sb.companyId)
                  Sync.removeCompany(sb.companyId)
                }
              })
            } catch (e) {
              logger.error(e)
            }
          }

          if (json.paymentStatuses && json.paymentUsersStatus) {
            try {
              PaymentClient.loadAll(
                json.paymentStatuses,
                json.paymentUsersStatus
              )
            } catch (e) {
              logger.error(e)
            }
          }

          if (json.companies) {
            json.companies
              .filter(data => !!data)
              .forEach(c => {
                try {
                  this.processSandboxData(c)
                } catch (e) {
                  logger.error(e)
                }
              })

            SyncAutoSaver.update()
          }
          // json.companies.forEach(c => SyncEvents.triggerAll(c.id));
          Connector.cleanup()
          resolve(true)
        })
        .catch(e => {
          logger.warn(e)
          resolve(false)
        })
        .then(() => {
          Accounts.trigger([], true)
          Sync.actualizing = false
        })
    })
  }

  static applyPatch(
    companyId: DataObjectId,
    toRevision: number,
    patch: {[id: string]: DataObjectJSON},
    type: 'silent' | 'all' | 'exact'
  ) {
    const box = Sync.get({companyId})
    if (!toRevision || !patch || !box || box.revision > toRevision) {
      Backend.report('Incorrect patch', companyId, box?.revision, toRevision)
    }
    logger.log('Patch', Object.keys(patch).length, 'type=' + type)
    box.revision = toRevision

    const rd = Sync.latestRemoteData.get(companyId)
    if (rd) {
      rd.revision = toRevision
    }

    const ids: DataObjectId[] = []
    const upIds: Set<DataObjectId> = new Set()
    let shouldRebuildTreeIndex = false
    Object.keys(patch).forEach((id: DataObjectId) => {
      const json = patch[id]
      upIds.add(id)

      const entJson = json as EntityJSON
      if (entJson?.location) {
        upIds.add(entJson.location)
      }
      try {
        ids.push(id)
        rd.index[id] = Util.clone(json)
        const obj = Sync.registeredObjects.get(companyId).get(id)
        if (obj) {
          if (obj instanceof Entity) {
            if (obj.location !== (json as EntityJSON).location) {
              shouldRebuildTreeIndex = true
            }
          } else if (obj instanceof Connections) {
            box.removeForwardConnection(obj)
            box.importForwardConnection(obj)
          }
          const wasLazy = obj.isLazy
          obj.fromJSON(companyId, json)
          if (wasLazy && !json.isLazy && type === 'exact') {
            obj.isLazy = false
          } else {
            obj.isLazy = !!wasLazy
          }
        } else {
          Sync.processRemoteObjectAddition(companyId, json)
        }
      } catch (e) {
        logger.error('Failed to add object to storage', id, patch[id], e)
      }
    })
    if (shouldRebuildTreeIndex) {
      box.rebuildTreeIndex()
    }
    if (['exact', 'all'].includes(type)) {
      SyncEvents.trigger(companyId, [...upIds], true)
    }
    if (type === 'all') {
      SyncEvents.triggerAll(companyId)
    }
  }

  private static initSearchIndices(
    sandboxes: Map<DataObjectId, SandboxTree>
  ): void {
    const searchIndices: Map<DataObjectId, Search> = new Map()
    sandboxes.forEach(s => {
      searchIndices.set(s.companyId, new Search(s.companyId))
    })
    Sync.searchIndices = searchIndices
    searchIndices.forEach(s => s.buildIndex())
  }

  private static invokeCallback(
    resp: GenResp,
    callback?: (resp: GenResp) => void
  ): void {
    if (callback) {
      callback(resp)
    } else {
      Sync.onReload?.()
    }
  }

  public static callReload(companyId?: DataObjectId): void {
    Sync.onReload?.(companyId)
  }

  private static initConnector(): void {
    /* Listen to remote changes and apply them */
    void Connector.init({
      userId: Sync.userId,
      key: Sync.key,
      onBranch: branch => Sync.processRemoteChanges(branch),
      onApprove: Sync.processBranchApprove
    })
  }

  public static onConnected(): void {
    if (Sync.unapprovedChanges.size > 0) {
      logger.log('Processing unapproved', [...Sync.unapprovedChanges.keys()])

      const changes = [...Sync.unapprovedChanges]
      changes.forEach(v => {
        if (v[1]?.approved) {
          logger.log('Deleted approved changes')
          Sync.unapprovedChanges.delete(v[0])
          return
        }
        const r = JSON.stringify(v)
        if (r.length > 1000000) {
          Backend.report('Skipped unapproved', globals.currentUserId, r.length)
          Sync.unapprovedChanges.delete(v[0])
        }
      })

      Sync.unapprovedChanges.forEach(br => {
        Sync.commitDebouncer.process(br)
      })
    }
  }

  /**
   * Retrieves all needed data and initiates synchronization of all the stored
   * objects.
   */
  public static async init(options: SyncInitOptions): Promise<void> {
    const {
      userId,
      key,
      callback,
      onReload = Util.noop,
      onBeforeReload = Util.noop,
      cacheService,
      selectedCompanyIds
    } = options

    Sync.userId = userId
    Sync.key = key
    Sync.onReload = onReload
    Sync.onBeforeReload = onBeforeReload
    Sync.selectedCompanyIds = selectedCompanyIds

    Cache.init(cacheService)

    /* Send local changes to the server but not very often (only one request
       within 300ms) */
    Sync.commitDebouncer = new Debouncer<Branch>(300)
    Sync.commitDebouncer.onReady((br: Branch[]) => Sync.processLocalChanges(br))

    Sandbox.initializer = (_: Sandbox, obj: DataObject) => {
      /* Each object in any sandbox must be registered for
       synchronization */
      Sync.register(obj)
    }

    return Sync.reload({
      initial: true,
      callback,
      shouldUseCachedData: true
    })
  }

  public static async cleanCachedData(companyId?: DataObjectId): Promise<void> {
    if (!companyId) {
      try {
        await Cache.cleanAll()
      } catch (e) {
        logger.error('Error in cleanCachedData', e)
      }
      return
    }
    if (!Backend.features.webDataCache) {
      return Promise.resolve()
    }
    if (Cache.isValid()) {
      return companyId ? Cache.clean(companyId) : Cache.cleanAll()
    }
  }

  private static async loadData(
    companyIds: ReadonlyArray<DataObjectId>
  ): Promise<{response: IndexResp; hasRemoteData: boolean} | undefined> {
    if (!Backend.features.webDataCache) {
      return Promise.resolve(undefined)
    }
    try {
      const cachedMetadataDump = await Cache.readMetadata()
      if (!cachedMetadataDump) {
        return undefined
      }
      const cachedMetadata: IndexResp = JSON.parse(cachedMetadataDump)
      if (
        cachedMetadata.checksum !== Util.checksum(Backend.loadKey()) ||
        !cachedMetadata.availableCompanies
      ) {
        await Sync.cleanCachedData()
        return undefined
      }

      const selectedCompanyIds =
        companyIds || cachedMetadata.availableCompanies.map(v => v.companyId)
      const dumps = await Promise.all(
        selectedCompanyIds.map(async id => ({
          id,
          dump: await Sync.readCache(id)
        }))
      )

      const [cachedDumps, unfoundDumps] = ArrUtil.partition(
        dumps,
        ({dump}) => dump !== undefined
      )

      const cachedData = cachedDumps.map(({dump}) => dump)

      if (unfoundDumps.length === 0) {
        const response = {
          ...cachedMetadata,
          data: cachedData
        }

        return {response, hasRemoteData: false}
      } else {
        logger.warn('Not all companies found in cache, loading…', unfoundDumps)
        const {data: remoteData, ...remoteMetadata} = await Sync.loadRemoteData(
          unfoundDumps.map(d => d.id)
        )

        const response = {
          ...remoteMetadata,
          data: [...cachedData, ...remoteData]
        }

        return {response, hasRemoteData: true}
      }
    } catch (e) {
      logger.warn('Error while restoring cache', e)
      return Promise.resolve(undefined)
    }
  }

  private static async writeCache(s: SandboxJSON): Promise<void> {
    if (!s) {
      return Promise.resolve()
    }
    try {
      await Cache.write(s.companyId, s as any) // can write object
    } catch (e) {
      logger.error(e)
    }
  }

  private static async readCache(
    companyId: DataObjectId
  ): Promise<SandboxJSON | undefined> {
    const cache = (await Cache.read(companyId)) as any
    if (typeof cache === 'string') {
      return JSON.parse(cache)
    } else if (typeof cache === 'object') {
      return cache
    } else {
      return undefined
    }
  }

  public static async saveCachedData(): Promise<void> {
    if (!Backend.features.webDataCache) {
      return Promise.resolve()
    }
    if (!Sync.canSaveCache || !Cache.isValid() || Sync.reloading) {
      return Promise.resolve()
    }

    if (
      Sync.saveCacheInProgress &&
      Date.now() - Sync.saveCacheInProgress < writeCacheTimeout
    ) {
      logger.log('Save cache in progress, skipping…')
      return Promise.resolve()
    }
    Sync.saveCacheInProgress = Date.now()

    const t1 = Date.now()
    const news = Sync.latestRemoteData.get(newsCompanyId)

    const metadata: IndexResp = {
      result: 'ok',
      apps: MarketplaceClient.apps,
      isNewsEditor: Sync.isNewsEditor,
      phone: globals.phone,
      registrationType: globals.registrationType,
      news,
      standalone: globals.standalone,
      readonly: globals.readonly,
      data: undefined,
      profileId: Sync.profileId,
      accounts: Accounts.accounts(),
      availableCompanies: Sync.listSandboxes().map(sb => ({
        companyId: sb.companyId,
        companyName: Option.from(sb.get(sb.companyId))
          .map(c => c.title)
          .ifEmpty(() => logger.error('No company hub', sb.companyId))
          .getOrElse('-')
      })),
      permissions: undefined,
      ...PaymentClient.serializaAll(),
      checksum: Util.checksum(Backend.loadKey())
    }

    await Cache.writeMetadata(JSON.stringify(metadata))
    for (const s of Sync.latestRemoteData.values()) {
      if (s.companyId !== newsCompanyId) {
        try {
          await Sync.writeCache(s)
        } catch (e) {
          logger.error(e)
        }
      }
    }

    Sync.saveCacheInProgress = undefined

    const t2 = Date.now()
    if (t2 - t1 > 1000) {
      logger.log('Dump saved in ' + (t2 - t1) + 'ms')
    }
  }

  static saveUnapproved(): void {
    const json: UnapprovedSerialized = {
      checksum: Util.checksum(Backend.loadKey()),
      changes: {}
    }
    Sync.unapprovedChanges.forEach(br => {
      if (!br.approved) {
        json.changes[br.id] = br.toJSON()
      }
    })
    const jsonStr = JSON.stringify(json)
    try {
      Backend.saveLocal(unapprovedChangesKey, jsonStr)
    } catch (e) {
      logger.log('Changes length', jsonStr.length)
      logger.warn('Unapproved changes persistence fail', (e as Error).message)
    }
  }

  private static cleanUnapproved(): void {
    Backend.removeLocal(unapprovedChangesKey)
  }

  private static restoreUnapproved(): void {
    const str = Backend.loadLocal(unapprovedChangesKey)
    if (str) {
      try {
        const obj: UnapprovedSerialized = JSON.parse(str)
        if (obj.checksum !== Util.checksum(Backend.loadKey())) {
          Sync.cleanUnapproved()
          return
        }
        Sync.unapprovedChanges = new Map(
          Object.keys(obj.changes).map(
            id => [id, Branch.fromJSON(obj.changes[id])] as [UUID, Branch]
          )
        )
        Sync.unapprovedChanges.forEach(b => Sync.localBranchesIds.add(b.id))
      } catch (e) {
        logger.error(e)
        Sync.cleanUnapproved()
      }
    }
  }

  public static async removeCompany(companyId: DataObjectId): Promise<void> {
    if (!Sync.sandboxes.has(companyId)) {
      logger.log('Company not found', companyId)
    }
    const currentId = globals.getCompanyId()
    Sync.sandboxes.delete(companyId)
    Sync.latestRemoteData.delete(companyId)
    ;[...Sync.unapprovedChanges.values()].forEach(v => {
      if (v.companyId === companyId) {
        Sync.unapprovedChanges.delete(v.id)
      }
    })
    Sync.saveUnapproved()
    await Sync.cleanCachedData(companyId)
    await Sync.saveCachedData()
    if (currentId === companyId) {
      Company.setFirstCompany()
    }
    SyncEvents.triggerAll(companyId)
  }

  private static handleCommit(obj: DataObject, forced: boolean): void {
    if (!Storage.getItem(wasLicenseWarningShownKey)) {
      const isFreeTariff = PaymentClient.isFree(globals.getCompanyId())
      if (isFreeTariff) {
        const usersCount = getUserCount()
        const maxUsersCount = getLicenseUserCount()
        if (usersCount > maxUsersCount) {
          const isUserAdmin = PermissionsChecker.isAdmin()
          const message = isUserAdmin
            ? Lang.msg.freeTariffWarningForAdmin
            : Lang.msg.freeTariffWarning
          MessageBox.warn(message, {
            timer: 5 * 60 * 1000
          })
          Storage.setItem(wasLicenseWarningShownKey, '1')
          return
        }
      }
    }
    SyncCommitHandler.onDataObjectCommit(obj, forced)
  }

  /**
   * Register object to Sync in order to track its changes.
   */
  private static register(dataObject: DataObject): void {
    let obj = Sync.registeredObjects.get(dataObject.companyId)
    if (!obj) {
      obj = new Map()
      Sync.registeredObjects.set(dataObject.companyId, obj)
    }
    if (obj?.has(dataObject.id)) {
      if (dataObject !== obj.get(dataObject.id)) {
        logger.warn('Tried to register object more than once')
      }
      return
    }

    obj.set(dataObject.id, dataObject)
    dataObject.onCommit(this.handleCommit)
  }

  /**
   * Processes multiple local branches. There can be more than one local branch
   * because of debounce delays.
   */
  private static processLocalChanges(data: Branch[]): void {
    /* Concatenated data and unprocessedChanges without duplicate branches */
    data.forEach(br => Sync.localBranchesIds.add(br.id))
    const allUnprocessed: Map<UUID, Branch> = new Map([
      ...Sync.unprocessedChanges
    ])
    data.forEach(br => allUnprocessed.set(br.id, br))
    Sync.unprocessedChanges.clear()

    /* Merged by companyId */
    const mergedBranches: Map<DataObjectId, Branch> = new Map()
    allUnprocessed.forEach(br => {
      if (Sync.unapprovedChanges.has(br.id)) {
        Sync.unapprovedChanges.delete(br.id)
      }
      const mb = mergedBranches.get(br.companyId)
      if (!mb) {
        mergedBranches.set(br.companyId, br)
      } else {
        mb.id = Util.UUID()
        if (mb.fromRevision !== br.fromRevision) {
          logger.warn('Local fromRevisions are not expected to be different')
          mb.fromRevision = Math.max(mb.fromRevision, br.fromRevision)
        }
        if (mb.toRevision || br.toRevision) {
          logger.error('Local toRevisions are not expected to be set')
          mb.toRevision = undefined
        }
        Branch.walkThroughChanges(br, (id, diff) => {
          mb.changes[id] = mb.changes[id]
            ? Diff.merge(mb.changes[id], diff)
            : diff
        })
      }
    })
    mergedBranches.forEach(mb => {
      const r = JSON.stringify(mb)
      const sizeLimit =
        globals.standalone || globals.devMode ? 15_000_000 : 10_000_000
      if (r.length > sizeLimit) {
        MessageBox.show('Large request skipped', 'danger')
        Backend.report('Large branch skipped', globals.currentUserId, r.length)
        return
      }
      mb.attempts = (mb.attempts || 0) + 1
      if (mb.attempts > 30) {
        Backend.report('Branch attempts limit reached', globals.currentUserId)
        return
      }
      Sync.localBranchesIds.add(mb.id)
      Sync.unapprovedChanges.set(mb.id, mb)
      Connector.commit(mb)
    })
    Sync.saveUnapproved()
  }

  private static processBranchApprove(branchId: UUID): void {
    const change = Sync.unapprovedChanges.get(branchId)
    if (change) {
      change.approved = true
      Sync.saveUnapproved()
    }
  }

  /**
   * Process updates from server: it applies branches and then fires update
   * event.
   */
  private static processRemoteChanges(branch: BranchJSON): void {
    if (Sync.processedBranchesIds.has(branch.id)) {
      logger.log('Skipping already processed branch', branch.id)
      return
    }
    const updEventData: SyncUpdate[] = []
    const isLocalBranch = Sync.localBranchesIds.has(branch.id)
    if (isLocalBranch) {
      Sync.unapprovedChanges.delete(branch.id)
      Sync.saveUnapproved()
    }
    logger.log('process remote change', branch.id, isLocalBranch, branch)
    const lrdBox = Sync.latestRemoteData.get(branch.companyId)
    ensure(lrdBox, 'remote data must be defined')
    lrdBox.revision = branch.toRevision

    Branch.walkThroughChanges(branch, (id: DataObjectId, diff: Diff) => {
      const locJSON = lrdBox.index[id]
      const storedObject = Sync.registeredObjects.get(branch.companyId).get(id)
      const allowedPaths = storedObject?.isLazy
        ? LAZY_OBJECT_DIFF_PATHS
        : undefined
      const {result, changed} = Diff.applyInPlace(
        diff,
        lrdBox.index[id],
        allowedPaths
      )
      lrdBox.index[id] = result
      if (changed) {
        updEventData.push(
          SyncEvents.updFromObjectAndDiff(
            locJSON ? storedObject : undefined,
            diff,
            {
              fromRemote: !isLocalBranch,
              companyId: branch.companyId
            }
          )
        )
      }

      const addr = DataObjectAddress.create(branch.companyId, id)
      if (diff.isNew) {
        const handler = Sync.serverCreateApproveHandlers.get(addr)
        if (handler) {
          handler()
          Sync.serverCreateApproveHandlers.delete(addr)
        }
      }
      Sync.serverUpdateApproveHandlers.get(addr)?.forEach(h => h())
    })
    if (!isLocalBranch) {
      Sync.applyBranch(branch)
    }
    SyncAutoSaver.update()
    Sync.processedBranchesIds.add(branch.id)

    if (updEventData.length > 0) {
      SyncEvents.triggerUpdate(branch.companyId, updEventData, true)
    }
  }

  private static applyBranch(branch: BranchJSON): void {
    Branch.walkThroughChanges(branch, (id: DataObjectId, diff: Diff) => {
      try {
        const obj = Sync.registeredObjects.get(branch.companyId)?.get(id)
        const wasLazy = obj?.isLazy
        const allowedPaths = obj?.isLazy ? LAZY_OBJECT_DIFF_PATHS : undefined
        if (obj) {
          // local object update
          const json = Util.clone(obj.toJSON())
          if (obj instanceof Connections) {
            const newJson = Diff.apply(
              diff,
              json,
              allowedPaths
            ) as ConnectionsJSON
            Sync.get(branch).removeForwardConnection(obj)
            obj.fromJSON(branch.companyId, newJson)
            Sync.get(branch).importForwardConnection(obj)
          } else if (obj instanceof Entity) {
            const oldLoc = (json as EntityJSON).location
            const newJson = Diff.apply(diff, json, allowedPaths) as EntityJSON
            obj.fromJSON(branch.companyId, newJson)
            obj.isLazy = !!wasLazy
            if (obj.location !== oldLoc) {
              Sync.get(branch).moveTreeNode(
                obj,
                oldLoc,
                obj.location,
                false,
                true
              )
            }
          }
        } else {
          // new object
          if (!diff.isNew || !diff.newObj) {
            logger.error('Received an update of an unknown object', id, diff)
          } else {
            Sync.processRemoteObjectAddition(branch.companyId, diff.newObj)
          }
        }
      } catch (e) {
        logger.error('Failed to apply diff', id)
      }
    })
  }

  /**
   * Add remote object into the corresponding sandbox
   */
  private static processRemoteObjectAddition(
    companyId: DataObjectId,
    json: DataObjectJSON
  ): void {
    const locBox = Sync.get({companyId})
    const obj = SandboxTree.jsonToObject(companyId, json)
    if (obj instanceof Entity) {
      if (locBox.has(obj.id)) {
        throw new Error('Sync: Entity already exists')
      }
      locBox.add(obj, obj.location, false, true)
    } else if (obj instanceof Connections) {
      if (locBox.forwardConnections(obj.from).size > 0) {
        logger.error('Sync: Connection already exists')
        return
      }
      locBox.importForwardConnection(obj)
    } else {
      logger.error('Unknown object type', json)
      return
    }
  }

  public static parentHub(hub: HubLike): Hub {
    const box = Sync.get(hub)
    Util.assert(box !== undefined, 'Sync.parentHub - no sandbox')
    const item = box.get(hub.id)
    Util.assert(item !== undefined, 'Sync.parentHub - invalid hub')
    const parent = box.get(item.location)
    return parent as Hub
  }

  public static getPendingCompanies(): {
    companyId: DataObjectId
    companyName: string
  }[] {
    const pendingCompanies = Sync.pendingCompanies || []
    const availableCompanies = Sync.availableCompanies || []

    const pendingWithNames = ArrUtil.filterUndefined(
      pendingCompanies.map(cId =>
        availableCompanies.find(c => c.companyId === cId)
      )
    )

    return pendingWithNames
  }
}
